import { useShopI18n } from '../i18n/ShopI18n'
import { claimDailyGift, type DailyGift } from '../services/dailyGiftApi'
import { CurrentUserInfo, ShopItem } from '../types'
import { useEffect, useEffectEvent, useRef, useState } from 'react'

import CheckoutModal from './CheckoutModal'
import LoginForm from './LoginForm'
import { cancelCheckout, getCheckout } from '../services/api'
import { getShopData, type ShopPromotions } from '../services/shopApi'
import { useTheme } from '../contexts/ThemeContext'
import ShopCatalog from './ShopCatalog'
import PurchaseFlow from './PurchaseFlow'
import PurchaseHistory, { type PurchaseRecord } from './PurchaseHistory'
import { redeemCoupon } from '../services/couponApi'
import { redeemUltimatePass } from '../services/stampCardApi'
import { getPurchaseHistory } from '../services/purchaseHistoryApi'
import PaymentResult from './PaymentResult'
import type { PaymentOutcome } from './PaymentResult'
import { checkAuthentication, getCurrentUserInfo } from '../services/mobilePaymentBarApi'
import { formatShopAmount } from '../utils/shopFormatters'
import { logger } from '../utils/logger'
import { authService } from '../services/auth'
import { itemFromCheckout } from '../utils/checkoutReceipt'

function MobileShop() {
    const { t, locale, applyLocalization } = useShopI18n()
    const { themeName } = useTheme()
    const [promotions, setPromotions] = useState<ShopPromotions>()
    const [returnToGameUrl, setReturnToGameUrl] = useState<string>()
    const cancellationInFlight = useRef(false)
    const [cancellingCheckout, setCancellingCheckout] = useState(false)
    const [checkoutError, setCheckoutError] = useState<string | null>(null)
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
    const [checkoutUrl, setCheckoutUrl] = useState('')
    const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null)
    const [purchasedItem, setPurchasedItem] = useState<ShopItem | undefined>()
    const [outcome, setOutcome] = useState<PaymentOutcome | null>(null)
    const [reference, setReference] = useState<string | undefined>()
    const lastStampTotal = useRef<number | undefined>(undefined)
    const [redeeming, setRedeeming] = useState(false)
    const loggingOut = useRef(false)
    const [rewardNotice, setRewardNotice] = useState<string | null>(null)
    const [earnedStamp, setEarnedStamp] = useState<number | undefined>()
    const [history, setHistory] = useState<PurchaseRecord[] | undefined>()
    const [historyNextOffset, setHistoryNextOffset] = useState<number | null>(null)
    const [historyLoading, setHistoryLoading] = useState(false)
    const [historyError, setHistoryError] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [dailyGift, setDailyGift] = useState<DailyGift | null>(null)
    const [chips, setChips] = useState<ShopItem[]>([])
    const [hotDeals, setHotDeals] = useState<ShopItem[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [shopError, setShopError] = useState<string | null>(null)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
    const [authError, setAuthError] = useState<string | null>(null)
    // Bumped after a successful login to re-run the auth + data flow
    const [currentUser, setCurrentUser] = useState<CurrentUserInfo | null>(null)
    const [authAttempt, setAuthAttempt] = useState(0)

    const fetchShopData = async (refresh = false) => {
        try {
            const [data, user] = await Promise.all([getShopData({ refresh }), getCurrentUserInfo({ refresh })])
            if (refresh && lastStampTotal.current !== undefined && (user.stampCard?.total ?? 0) > lastStampTotal.current) {
                setEarnedStamp(user.stampCard?.count)
            }
            lastStampTotal.current = user.stampCard?.total
            if (data.localization) applyLocalization(data.localization)
            else if (user.locale) applyLocalization({ locale: user.locale, messages: {} })
            setCurrentUser(user)
            logger.log('SHOP', 'Received shop data', {
                hasChips: !!data.chips,
                chipsCount: data.chips?.length || 0,
                chipsSample: data.chips?.slice(0, 2),
                hasJokers: !!data.jokers,
                jokersCount: data.jokers?.length || 0,
                jokersSample: data.jokers?.slice(0, 2),
                hasHotDeals: !!data.hotDeals,
                hotDealsCount: data.hotDeals?.length || 0,
                hotDealsSample: data.hotDeals?.slice(0, 2),
                hasPaymentBarData: !!data.mobilePaymentBar,
                fullData: data,
            })
            
            setPromotions(data.promotions)
            setReturnToGameUrl(data.returnToGameUrl)
            setDailyGift(data.dailyGift ?? null)
            setChips(data.chips || [])
            setHotDeals(data.hotDeals || [])
            setShopError(null)
            
            logger.log('SHOP', 'State updated', {
                chipsSet: data.chips?.length || 0,
                jokersSet: data.jokers?.length || 0,
                hotDealsSet: data.hotDeals?.length || 0,
            })
        } catch (error) {
            logger.error('SHOP', 'Failed to load shop data:', error)
            // Keep empty arrays on error
            setDailyGift(null)
            setChips([])
            setHotDeals([])
            setShopError("Bitte prüfe deine Verbindung und versuche es erneut.")
        } finally {
            setIsLoading(false)
        }
    }

    // Check authentication and handle login if needed
    const initializeAuth = useEffectEvent(async () => {
        try {
            setIsLoading(true)
            setAuthError(null)

            // Extract token from URL if not already extracted (in case App.tsx hasn't run yet)
            // This is safe because extractAndRemoveTokenFromUrl is idempotent - it only extracts once
            const token = authService.extractAndRemoveTokenFromUrl()

            // Wait for any login in progress (from App.tsx or elsewhere)
            await authService.waitForLogin()

            // Check if we have a token in the auth service
            const hasToken = authService.hasToken()

            // A token in the URL still takes precedence, so deep links opened
            // from the game client log straight in without a form.
            if (hasToken) {
                logger.log('AUTH', 'Token found, attempting login', { tokenExtracted: !!token })
                try {
                    await authService.loginWithToken()
                    logger.log('AUTH', 'Login successful')
                } catch (error) {
                    // A bad or expired token is no longer a dead end - fall
                    // through to the login form and explain why.
                    logger.error('AUTH', 'Login failed:', error)
                    setAuthError("Your session link is no longer valid. Please sign in to continue.")
                    setIsAuthenticated(false)
                    setIsLoading(false)
                    return
                }
            }

            // Check authentication status
            const authenticated = await checkAuthentication()
            setIsAuthenticated(authenticated)

            if (!authenticated) {
                logger.warn('AUTH', 'User is not authenticated, showing login form')
                setIsLoading(false)
                return
            }

            // If authenticated, fetch shop data
            await fetchShopData()
        } catch (error) {
            logger.error('AUTH', 'Authentication check failed:', error)
            setAuthError("Authentication check failed. Please try again.")
            setIsAuthenticated(false)
            setIsLoading(false)
        }
    })
    useEffect(() => { void initializeAuth() }, [authAttempt])

    // Provider success may arrive before the payment webhook has booked the cart.
    useEffect(() => {
        if (outcome !== 'success' && outcome !== 'pending') return
        let active = true
        let timer: ReturnType<typeof setTimeout>
        let attempts = 0
        const refresh = async () => {
            try {
                const user = await getCurrentUserInfo({ refresh: true })
                if (!active) return
                if (lastStampTotal.current !== undefined && (user.stampCard?.total ?? 0) > lastStampTotal.current) {
                    setEarnedStamp(user.stampCard?.count)
                }
                lastStampTotal.current = user.stampCard?.total
                setCurrentUser(user)
            } catch {
                // Keep the last confirmed data; a later poll or return to the shop retries.
            }
            if (active && ++attempts < 5) timer = setTimeout(() => void refresh(), 2000)
        }
        timer = setTimeout(() => void refresh(), 2000)
        return () => { active = false; clearTimeout(timer) }
    }, [outcome])

    const handlePurchaseClick = async (item: ShopItem) => {
        setSelectedItem(null)
        setPurchasedItem(item)
        setOutcome(null)
        setIsCheckoutOpen(true)
        setCheckoutUrl('')
        setCheckoutError(null)

        try {
            const checkout = await getCheckout({
                referer: 'mobile-shop',
                id: item.cartId,
                slug: item.slug,
            })
            setPurchasedItem(itemFromCheckout(item, checkout))
            setCheckoutUrl(checkout.url)
        } catch (error) {
            logger.error('SHOP', 'Failed to load checkout:', error)
            setIsCheckoutOpen(false)
            setCheckoutError(error instanceof Error ? t(error.message) : t("Checkout konnte nicht geladen werden."))
            void fetchShopData(true)
        }
    }

    const handleCancelCheckout = async (id: string) => {
        if (cancellationInFlight.current) return
        cancellationInFlight.current = true
        setCancellingCheckout(true)
        setCheckoutError(null)
        try {
            await cancelCheckout(id)
            setPromotions(current => current ? { ...current, pendingFirstPurchase: null } : current)
            await fetchShopData(true)
        } catch (error) {
            setCheckoutError(error instanceof Error ? t(error.message) : t('Bitte versuche es erneut.'))
        } finally {
            cancellationInFlight.current = false
            setCancellingCheckout(false)
        }
    }

    const handleCloseCheckout = () => {
        setIsCheckoutOpen(false)
        setCheckoutUrl('')
    }

    const showResult = (result: PaymentOutcome, refId?: string, transactionId?: string) => {
        setEarnedStamp(undefined)
        setReference(refId || transactionId)
        setOutcome(result)
        setShowHistory(false)
        window.scrollTo({ top: 0 })
        if (result === 'success' || result === 'pending') void fetchShopData(true)
    }
    const redeemFreePass = async () => {
        const cycle = currentUser?.stampCard?.nextCycle
        if (!cycle || redeeming) return
        setRedeeming(true)
        setRewardNotice(null)
        try {
            setCurrentUser(await redeemUltimatePass(cycle))
            setRewardNotice("Dein Gratis-Ultimate-Pass liegt jetzt in deinem Inventar im Spiel.")
        } catch (error) {
            setRewardNotice(error instanceof Error ? t(error.message) : t("Bitte versuche es erneut."))
        } finally {
            setRedeeming(false)
        }
    }

    const openHistory = async (offset = 0) => {
        setShowHistory(true)
        setOutcome(null)
        setHistoryLoading(true)
        setHistoryError(false)
        try {
            const page = await getPurchaseHistory(offset)
            setHistory(previous => offset ? [...(previous ?? []), ...page.records] : page.records)
            setHistoryNextOffset(page.nextOffset)
        } catch {
            setHistoryError(true)
        } finally {
            setHistoryLoading(false)
        }
    }
    const backToShop = () => {
        setOutcome(null)
        setShowHistory(false)
        void fetchShopData(true)
        window.scrollTo({ top: 0 })
    }

    if (isLoading) {
        return <ShopCatalog chips={[]} jokers={[]} hotDeals={[]} onPurchase={() => undefined} loading />
    }

    // Not authenticated - offer email/password login rather than a dead end
    if (isAuthenticated === false || (isAuthenticated !== true && authError)) {
        return (
            <LoginForm
                notice={authError}
                onSuccess={() => setAuthAttempt((attempt) => attempt + 1)}
            />
        )
    }

    // Only render shop content if authenticated
    if (isAuthenticated !== true) {
        return null
    }


    // Excluded subTypes that should not be displayed
    const excludedSubTypes = ['emitter']
    const shouldExcludeItem = (item: ShopItem) => {
        // Exclude items with excluded subTypes
        if (excludedSubTypes.includes(item.subType || '')) {
            return true
        }
        // Exclude items where subType is 'offer' AND isOfferLayer is true
        if (item.subType === 'offer' && item.isOfferLayer === true) {
            return true
        }
        return false
    }

    // Filter out excluded subTypes before further processing
    const filteredChips = (chips || []).filter(item => !shouldExcludeItem(item) && item.slug !== 'regular-shop-bonus-chips')
    if (dailyGift) filteredChips.unshift({ slug: 'regular-shop-bonus-chips', tileSlug: 'regular-shop-bonus-chips', subType: 'bonus', type: 'chips', amount: dailyGift.amount, nextBookTime: dailyGift.nextBookTime, bgSlug: null, order: 0 })
    const logout = async () => {
        if (loggingOut.current) return
        loggingOut.current = true
        try {
            await authService.logout()
            // A fresh document also closes sockets and discards in-flight account requests.
            window.location.reload()
        } catch {
            setCheckoutError(t('Bitte prüfe deine Verbindung und versuche es erneut.'))
            loggingOut.current = false
        }
    }

    const filteredHotDeals = (hotDeals || []).filter(item => !shouldExcludeItem(item))

    const returnToGame = returnToGameUrl && /^(https?:|jackpotde:|wildewilma:|merkur24:|slotigo:|partyhousefun:|leoplay:|wildz:|lounge777:|volcanoepicslots:)/i.test(returnToGameUrl)
        ? () => window.location.assign(returnToGameUrl) : undefined
    const pending = promotions?.pendingFirstPurchase
    const pendingItem = pending && [...chips, ...hotDeals].find(item => item.slug === pending.slug)

    return (
        <>
            <ShopCatalog
                chips={filteredChips}
                jokers={[]}
                hotDeals={filteredHotDeals}
                promotions={promotions}
                stampCard={currentUser?.stampCard}
                contentMode={outcome ? 'result' : showHistory ? 'page' : 'catalog'}
                onReturnToGame={returnToGame}
                banner={<>
                    {checkoutError && <p role="alert">{checkoutError}</p>}
                    {pending && <section className="shop-empty"><p>{t("Du hast einen begonnenen Kauf mit Erstkauf-Bonus.")}</p><div className="shop-pending-actions"><button className="shop-secondary" disabled={cancellingCheckout} onClick={() => void handlePurchaseClick({ ...(pendingItem || { slug: pending.slug, tileSlug: pending.slug, type: 'chips', amount: 0, bgSlug: null, order: 0 }), cartId: pending.cartId })}>{t("Kauf fortsetzen")}</button><button className="shop-secondary" disabled={cancellingCheckout} onClick={() => void handleCancelCheckout(pending.cartId)}>{t("Kauf abbrechen")}</button></div></section>}
                </>}
                calendarNotice={<>
                    {currentUser?.stampCard?.rewardReady && <section className="shop-empty"><b>{t("Dein nächster Ultimate Pass ist gratis!")}</b><p>{t("Hol ihn ab und aktiviere ihn in deinem Inventar im Spiel.")}</p><button className="shop-secondary" disabled={redeeming} onClick={() => void redeemFreePass()}>{redeeming ? t("Wird abgeholt …") : t("Gratis-Ultimate-Pass abholen")}</button></section>}
                    {rewardNotice && <p role="status">{t(rewardNotice)}</p>}
                </>}
                account={currentUser ? { name: currentUser.name || t("Mein Konto"), email: currentUser.email } : undefined}
                balance={currentUser?.wallet ? formatShopAmount(currentUser.wallet.chips, locale) : undefined}
                onRedeemCoupon={async code => {
                    const result = await redeemCoupon(code)
                    if (result.balance !== undefined) setCurrentUser(user => user ? { ...user, wallet: { ...user.wallet, chips: result.balance! } } : user)
                    void fetchShopData(true)
                    return result
                }}
                onClaimDailyGift={async () => {
                    const result = await claimDailyGift('chips')
                    setDailyGift(result.dailyGift)
                    setCurrentUser(result.currentUser)
                    const gift = result.dailyGift
                    if (!result.booked && (!gift || gift.nextBookTime <= Date.now() / 1000)) throw new Error(t("Bonus nicht verfügbar"))
                }}
                stamps={currentUser?.stampCard?.count}
                onPurchase={setSelectedItem}
                onHistory={() => void openHistory()}
                onLogout={() => void logout()}
                onBack={backToShop}
                error={shopError ? t(shopError) : null}
                onRetry={() => void fetchShopData(true)}
            >
                {showHistory ? <>
                    {historyLoading && !history ? <p role="status">{t("Käufe werden geladen …")}</p> : <PurchaseHistory records={history} onBack={backToShop} />}
                    {historyError && <p role="alert">{t("Kaufhistorie konnte nicht geladen werden.")} <button onClick={() => void openHistory()}>{t("Erneut versuchen")}</button></p>}
                    {historyNextOffset !== null && <button className="shop-secondary" disabled={historyLoading} onClick={() => void openHistory(historyNextOffset)}>{t("Weitere Käufe laden")}</button>}
                </> : outcome ? <PaymentResult balance={currentUser?.wallet?.chips} onReturnToGame={returnToGame} stamps={currentUser?.stampCard?.count} earnedStamp={earnedStamp} outcome={outcome} item={purchasedItem} reference={reference} onBack={backToShop} onHistory={() => void openHistory()} onRetry={purchasedItem ? () => { setOutcome(null); setSelectedItem(purchasedItem) } : undefined} /> : null}
            </ShopCatalog>
            {selectedItem && <div className="web-shop shop-dialog-theme" data-theme={themeName}><PurchaseFlow item={selectedItem} onClose={() => setSelectedItem(null)} onConfirm={handlePurchaseClick} /></div>}

            {/* Checkout Modal */}
            <CheckoutModal 
                isOpen={isCheckoutOpen}
                checkoutUrl={checkoutUrl}
                onClose={handleCloseCheckout}
                onSuccess={(ref, transaction) => showResult('success', ref, transaction)}
                onError={(ref, transaction) => showResult('error', ref, transaction)}
                onPending={(ref, transaction) => showResult('pending', ref, transaction)}
                onCancel={() => showResult('cancel')}
            />

        </>
    )
}

export default MobileShop
